import { useState } from 'react';
import { Box, Button, IconButton, Modal, Snackbar, TextField, Typography, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { tokens } from '../../theme';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: 400,
  bgcolor: '#1F2A40',
  borderRadius: '10px',
  boxShadow: 'rgba(31, 42, 64, 0.24) 0px 3px 8px',
  p: 4,
};

const EMPTY_FORM = { currentPassword: '', newPassword: '', confirmPassword: '' };

/** Lets the signed-in admin user change their own password. */
export default function ChangeAdminPassword({ open, handleClose }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const showToast = (message) => {
    setToastMessage(message);
    setOpenToast(true);
  };

  const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const closeModal = () => {
    setForm(EMPTY_FORM);
    handleClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.newPassword.length < 6) return showToast('New password must be at least 6 characters long');
    if (form.newPassword !== form.confirmPassword) return showToast('New password and confirm password do not match');

    setSaving(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_APILINK}/admin/change-password`, {
        method: 'PUT',
        headers: {
          'Content-type': 'application/json',
          token: localStorage.getItem('token'),
        },
        body: JSON.stringify({ currentPassword: form.currentPassword, newPassword: form.newPassword }),
      });
      const data = await res.json();
      showToast(data.message || 'Could not change password');
      if (data.success) closeModal();
    } catch (err) {
      showToast('Could not change password');
    } finally {
      setSaving(false);
    }
  };

  const handleCloseToast = (event, reason) => {
    if (reason === 'clickaway') return;
    setOpenToast(false);
  };

  return (
    <>
      <Modal open={open} onClose={closeModal}>
        <Box sx={style}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb="10px">
            <Typography variant="h4" component="h2">
              Change Password
            </Typography>
            <IconButton size="small" onClick={closeModal}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <form onSubmit={handleSubmit}>
            <Box display="grid" gap="12px">
              <TextField
                fullWidth
                variant="filled"
                type="password"
                label="Current Password"
                name="currentPassword"
                autoComplete="current-password"
                value={form.currentPassword}
                onChange={handleChange}
                required
              />
              <TextField
                fullWidth
                variant="filled"
                type="password"
                label="New Password"
                name="newPassword"
                autoComplete="new-password"
                helperText="At least 6 characters"
                value={form.newPassword}
                onChange={handleChange}
                required
              />
              <TextField
                fullWidth
                variant="filled"
                type="password"
                label="Confirm New Password"
                name="confirmPassword"
                autoComplete="new-password"
                value={form.confirmPassword}
                onChange={handleChange}
                required
              />
              <Button
                type="submit"
                disabled={saving}
                sx={{
                  marginTop: '6px',
                  width: 'fit-content',
                  marginInline: 'auto',
                  backgroundColor: colors.blueAccent[700],
                  color: colors.grey[100],
                  fontSize: '14px',
                  fontWeight: 'bold',
                  padding: '10px 30px',
                  '&:hover': {
                    backgroundColor: colors.blueAccent[800],
                  },
                }}
              >
                {saving ? 'Saving...' : 'Update Password'}
              </Button>
            </Box>
          </form>
        </Box>
      </Modal>
      <Snackbar
        open={openToast}
        autoHideDuration={5000}
        onClose={handleCloseToast}
        message={toastMessage}
        action={
          <IconButton size="small" aria-label="close" color="inherit" onClick={handleCloseToast}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </>
  );
}
